import Swiper from "swiper";
import { A11y, Autoplay, EffectFade, EffectCoverflow, Navigation, Pagination, Scrollbar, Grid } from "swiper/modules";

export const createSlider = (el, options = {}) => {
  const container = el.querySelector(".swiper");
  const sliderNavigation = el.querySelector(".slider-navigation");

  if (!container) return;

  const defaultOpts = {
    modules: [A11y, Navigation],
    loop: true,
  };

  const opts = { ...defaultOpts, ...options };

  if (sliderNavigation) {
    opts.navigation = {
      nextEl: el.querySelector(".slider-button-next"),
      prevEl: el.querySelector(".slider-button-prev"),
    };
  }

  return new Swiper(container, opts);
};

export const createSliderWithBullets = (el, config = {}) => {
  const sliderPagination = el.querySelector(".slider-pagination");
  const swiper = createSlider(el, {
    modules: [
      A11y,
      Autoplay,
      Pagination,
      config.effect === "fade" ? EffectFade : null,
      config.effect == "coverflow" ? EffectCoverflow : null,
    ].filter(Boolean),
    loop: true,
    speed: 800,
    effect: config.effect,
    fadeEffect: config.effect === "fade" ? { crossFade: true } : undefined,
    autoplay: {
      delay: config.autoplayDelay || 4000,
      pauseOnMouseEnter: config.pauseOnMouseEnter !== false,
    },
    slidesPerView: config.slidesPerView || 1.2,
    spaceBetween: config.spaceBetween || 0,
    centeredSlides: config.centeredSlides || false,
    breakpoints: config.breakpoints || {},
    pagination: {
      el: sliderPagination,
      clickable: true,
      renderBullet: getRenderBullet(config.color),
    },
  });

  if (sliderPagination) {
    addBulletProgressHandler(swiper, sliderPagination);
  }

  return swiper;
};

export const getRenderBullet =
  (color = "var(--color-highlight)") =>
  (index, className) => `
  <span class="${className}" style="flex: 1; height: 100%; position: relative; overflow: hidden;">
    <span class="position-bullet" style="position: absolute; top: 50%; right: -11px; transform: translate(-50%, -50%); width: var(--spacing-slider-bullet-size); height: var(--spacing-slider-bullet-size); background-color: #FFF; border-radius: 50%; z-index: 10;"></span>
    <span class="progress-track" style="position: absolute; top: 0; left: 0; height: 100%; width: 0; background-color: ${color}; z-index: 1;"></span>
  </span>`;

export const addBulletProgressHandler = (swiper, paginationEl) => {
  let currentSlideIndex = 0;

  swiper.on("slideChange", () => {
    const bullets = Array.from(paginationEl.querySelectorAll(".swiper-pagination-bullet"));
    const realIndex = swiper.realIndex;

    if (swiper.previousIndex > realIndex && realIndex === 0) {
      currentSlideIndex = 0;
      bullets.forEach((b) => {
        b.classList.remove("completed");
        b.querySelector(".progress-track").style.width = "0";
      });
    } else if (swiper.previousIndex < realIndex || (swiper.previousIndex === bullets.length - 1 && realIndex === 0)) {
      currentSlideIndex = Math.max(currentSlideIndex, realIndex);
    }

    bullets.forEach((bullet, index) => {
      const track = bullet.querySelector(".progress-track");
      bullet.classList.toggle("completed", index < realIndex);
      if (track) track.style.width = index < realIndex ? "100%" : "0";
    });
  });
};

const handleFeaturedSlider = (el) => {
  const numSlides = el.querySelectorAll(".swiper-slide");

  createSlider(el, {
    modules: [A11y, Autoplay, EffectFade, Navigation, Pagination, Scrollbar],
    effect: "fade",
    fadeEffect: { crossFade: true },
    loop: numSlides.length > 1,
    speed: 800,
    autoplay: {
      delay: 4000,
      pauseOnMouseEnter: true,
    },
  });
};

const handleRelatedStoriesSlider = (el) => {
  createSlider(el, {
    loop: false,
    slidesPerView: 1,
    spaceBetween: 20,
    breakpoints: {
      768: { slidesPerView: 2, spaceBetween: 20 },
      1440: { slidesPerView: 3, spaceBetween: 40 },
    },
  });
};

const handleFeaturedEventsSlider = (el) =>
  createSliderWithBullets(el, {
    color: "#A7AF39",
    effect: "fade",
  });

const handleSuccessStoriesSlider = (el) => {
  return createSliderWithBullets(el, {
    color: "#56C5D0",
    slidesPerView: 1.06,
    centeredSlides: true,
    effect: "coverflow",
    loopedSlides: 1,
    coverflowEffect: {
      rotate: 0,
      stretch: 0,
      depth: 500,
      modifier: 0.5,
      slideShadows: false,
    },
    spaceBetween: 40,
    breakpoints: {
      768: {
        slidesPerView: 1.04,
        spaceBetween: 60,
        coverflowEffect: {
          rotate: 0,
          stretch: 0,
          depth: 200,
          modifier: 0.5,
          slideShadows: false,
        },
      },
      1020: {
        slidesPerView: 1.01,
        spaceBetween: 210,
        coverflowEffect: {
          rotate: 0,
          stretch: 0,
          depth: 500,
          modifier: 0.5,
          slideShadows: false,
        },
      },
      1920: {
        slidesPerView: 1.01,
        spaceBetween: 210,
        centeredSlides: false,
        coverflowEffect: {
          rotate: 0,
          stretch: 0,
          depth: 500,
          modifier: 0.5,
          slideShadows: false,
        },
      },
    },
  });
};

const handleSingleTestimonialsSlider = (el) =>
  createSliderWithBullets(el, {
    color: "var(--color-highlight)",
    effect: "fade",
  });

const handleMultipleTestimonialsSlider = (el) =>
  createSliderWithBullets(el, {
    color: "var(--color-highlight)",
    effect: "slide",
    slidesPerView: 1.1,
    spaceBetween: 40,
    centeredSlides: true,
    breakpoints: {
      768: { centerInsufficientSlides: true, slidesPerView: 1.5 },
      1020: { centerInsufficientSlides: true, slidesPerView: 2.5 },
    },
  });

const handleBenefitsSlider = (el) =>
  createSlider(el, {
    modules: [A11y, Navigation, Grid],
    slidesPerView: 1,
    grid: {
      fill: "row",
      rows: 2,
    },
    loop: false,
    breakpoints: {
      768: { slidesPerView: 2 },
      1024: { slidesPerView: 4 },
    },
  });

const handleHeaderSlider = (el) => {
  const pauseBtn = el.querySelector('[data-slider-pause]');
  const playBtn = el.querySelector('[data-slider-play]');

  const slider = createSliderWithBullets(el, {
    color: "var(--color-highlight)",
    effect: "fade",
    slidesPerView: 1,
    spaceBetween: 0,
    autoplayDelay: 6000,
    pauseOnMouseEnter: false,
  });

  if (pauseBtn && playBtn) {
    pauseBtn.addEventListener('click', () => {
      slider.autoplay.pause();
      el.setAttribute('data-slider-paused', 'true');

      playBtn.classList.remove('hidden');
      pauseBtn.classList.add('hidden');
    });

    playBtn.addEventListener('click', () => {
      slider.autoplay.resume();
      el.setAttribute('data-slider-paused', 'false');

      pauseBtn.classList.remove('hidden');
      playBtn.classList.add('hidden');
    });
  }

  return slider;
};

const initializeSliders = (selector, handler) => {
  document.querySelectorAll(selector).forEach(handler);
};

export default {
  init: () => {
    initializeSliders("[data-related-nav-slider]", handleRelatedStoriesSlider);
    initializeSliders("[data-featured-slider]", handleFeaturedSlider);
    initializeSliders("[data-featured-events-slider]", handleFeaturedEventsSlider);
    initializeSliders("[data-success-stories-slider]", handleSuccessStoriesSlider);
    initializeSliders("[data-single-testimonials-slider]", handleSingleTestimonialsSlider);
    initializeSliders("[data-multiple-testimonials-slider]", handleMultipleTestimonialsSlider);
    initializeSliders("[data-benefits-slider]", handleBenefitsSlider);
    initializeSliders("[data-header-slider]", handleHeaderSlider);
  },
};
